import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import MetaTags from '../components/SEO/MetaTags';
import { useAdmin } from '../contexts/AdminContext';
import {
  CalculatorIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  UsersIcon,
  ClockIcon,
  CurrencyRupeeIcon,
  BuildingOffice2Icon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon,
  StarIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/solid';

const Home = () => {
  const { siteContent, layoutConfig } = useAdmin();
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: CalculatorIcon,
      title: 'Tax Planning & Filing',
      description: 'Comprehensive tax solutions for individuals and businesses with expert guidance on tax optimization.',
      link: '/services/tax',
    },
    {
      icon: DocumentTextIcon,
      title: 'GST Compliance',
      description: 'End-to-end GST services including registration, filing, and compliance management for seamless operations.',
      link: '/services/gst',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Audit & Assurance',
      description: 'Professional audit services ensuring compliance and providing valuable insights for business growth.',
      link: '/services/audit',
    },
    {
      icon: BuildingOffice2Icon,
      title: 'Business Registration',
      description: 'Hassle-free company registration services including Pvt Ltd, LLP, partnership, and more.',
      link: '/services/registration',
    },
  ];

  const features = [
    {
      icon: ClockIcon,
      title: 'Digital-First Approach',
      description: 'Modern, paperless processes with secure digital documentation and online consultations.',
    },
    {
      icon: UsersIcon,
      title: 'Expert Team',
      description: 'Certified chartered accountants with 10+ years of experience in Kerala taxation and compliance.',
    },
    {
      icon: CurrencyRupeeIcon,
      title: 'Transparent Pricing',
      description: 'Clear, upfront pricing with no hidden charges. Get quotes instantly for all services.',
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: '24/7 Support',
      description: 'Round-the-clock support via WhatsApp, email, and phone for all your queries.',
    },
  ];

  const testimonials = [
    {
      name: 'Mohammed Shafi',
      company: 'MS Traders',
      content: 'NAQD Consulting helped us save 40% on taxes through strategic planning. Their digital-first approach made everything seamless.',
      rating: 5,
    },
    {
      name: 'Priya Suresh',
      company: 'Tech Startup',
      content: 'Excellent service for GST compliance and business registration. They made complex processes simple and straightforward.',
      rating: 5,
    },
    {
      name: 'Abdul Rahman',
      company: 'NRK Services',
      content: 'As an NRI, I needed expert guidance on Indian taxation. NAQD provided comprehensive solutions with great clarity.',
      rating: 5,
    },
  ];

  return (
    <div>
      <MetaTags
        title={siteContent.hero?.title || "NAQD Consulting - Chartered Accountants in Malappuram, Kerala"}
        description={siteContent.hero?.subtitle || "Leading chartered accountancy firm in Malappuram, Kerala. Expert tax planning, GST compliance, business registration, and financial consulting services with digital-first approach."}
      />

      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/assets/images/backgrounds/finance-hero.png')" }}
      >
        {/* Background Overlay */}
        <div className="absolute inset-0 bg-naqd-primary/80"></div>

        <div className="container-custom text-center relative z-10">
          <div className="animate-on-scroll">
            <h1 className="heading-primary mb-6 text-white">
              {siteContent.hero?.title || "Expert Chartered Accountants in Malappuram, Kerala"}
            </h1>
            <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto font-light">
              {siteContent.hero?.subtitle || "Transform your financial future with Kerala's most trusted CA firm. We combine traditional expertise with modern digital solutions to deliver exceptional tax, compliance, and business advisory services."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="hover-shine bg-white text-naqd-primary hover:bg-gray-50 font-semibold py-3 px-8 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                {siteContent.hero?.cta || "Get Free Consultation"}
              </Link>
              <Link to="/services" className="hover-shine border-2 border-white/80 text-white hover:bg-white hover:text-naqd-primary font-semibold py-3 px-8 rounded-full transition-all duration-300 backdrop-blur-sm">
                Explore Services
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 animate-on-scroll">
            {[
              { value: '500+', label: 'Happy Clients' },
              { value: '10+', label: 'Years Experience' },
              { value: '95%', label: 'Client Retention' },
              { value: '₹50Cr+', label: 'Tax Saved' },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl font-bold text-naqd-green">{stat.value}</div>
                <div className="text-sm text-gray-200">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-12 animate-on-scroll">
            <h2 className="heading-secondary">
              Comprehensive <span className="text-gradient">Financial Services</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From tax planning to business registration, we offer complete financial
              solutions tailored for Kerala businesses and individuals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="card-hover bg-white p-8 rounded-2xl border border-gray-100 shadow-sm group animate-on-scroll"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-naqd-green/10 rounded-full mb-4 group-hover:bg-naqd-green/20 transition-colors">
                  <service.icon className="w-8 h-8 text-naqd-green" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                <Link
                  to={service.link}
                  className="text-naqd-green hover:text-naqd-light transition-colors flex items-center justify-center"
                >
                  Learn More
                  <ArrowRightIcon className="w-4 h-4 ml-1" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-on-scroll">
              <h2 className="heading-secondary">
                Why Choose <span className="text-gradient">NAQD Consulting?</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We're not just accountants – we're your strategic financial partners.
                Our unique approach combines deep local expertise with cutting-edge
                technology to deliver results that matter.
              </p>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-4 animate-on-scroll">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-naqd-green/10 rounded-lg flex items-center justify-center">
                        <feature.icon className="w-6 h-6 text-naqd-green" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-1">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="animate-on-scroll">
              <div className="bg-white shadow-xl border border-gray-100 rounded-xl p-8">
                <h3 className="text-2xl font-semibold mb-6">Ready to Get Started?</h3>
                <form className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Name</label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-naqd-primary focus:outline-none text-gray-800"
                      placeholder="Your Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email</label>
                    <input
                      type="email"
                      className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-naqd-primary focus:outline-none text-gray-800"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Service Needed</label>
                    <select className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:border-naqd-primary focus:outline-none text-gray-800">
                      <option>Select Service</option>
                      <option>Tax Planning & Filing</option>
                      <option>GST Compliance</option>
                      <option>Business Registration</option>
                      <option>NRI Tax Services</option>
                    </select>
                  </div>
                  <button type="submit" className="btn-primary w-full">
                    Get Free Quote
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12 animate-on-scroll">
            <h2 className="heading-secondary">
              What Our <span className="text-gradient">Clients Say</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="card animate-on-scroll"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} className="w-5 h-5 text-yellow-400" />
                  ))}
                </div>
                <blockquote className="text-gray-600 mb-4 italic">
                  "{testimonial.content}"
                </blockquote>
                <div>
                  <div className="font-semibold">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="container-custom text-center">
          <div className="max-w-3xl mx-auto animate-on-scroll">
            <h2 className="heading-secondary">
              Ready to <span className="text-gradient">Transform Your Finances?</span>
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Join 500+ satisfied clients who trust NAQD Consulting for their
              financial success. Schedule your free consultation today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                Book Free Consultation
              </Link>
              <a
                href="tel:+919074286763"
                className="btn-secondary text-lg px-8 py-4"
              >
                Call Now: +91 90742 86763
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;